import React from 'react'
import DatePicker from 'react-datepicker';

// Custom component for date array input
const DateArrayInput = ({ label, dates, onAddDate, onRemoveDate, onUpdateDate }) => {
  return (
    <div className="space-y-2">
      {dates && dates.map((date, index) => (
        <div key={index} className="flex items-center space-x-2">
          <DatePicker
            selected={date}
            onChange={(newDate) => onUpdateDate(index, newDate)}
            dateFormat="yyyy-MM-dd"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            type="button"
            onClick={() => onRemoveDate(index)}
            className="px-3 py-2 text-red-600 hover:text-red-800"
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={onAddDate}
        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
      >
        Add Date
      </button>
    </div>
  );
};

const AddEditForm = ({name, items, data, mode, closeForm, handleRegister, handleUpdate, handleInputChange, stateValue, handleDateChange, handleDateArrayChange, submitting, submitLabel}) => {
  const isEditMode = mode === 'edit';
  const isSubmitting = submitting === true;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (isEditMode) {
      handleUpdate(stateValue)
    } else {
      handleRegister(stateValue)
    }
  };

  return (
    <>
    <div className=''>
      <div className="left-1/2 flex h-100 flex-1 w-full flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto">
          <h2 className="text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            {name}
          </h2>
        </div>
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <form className="space-y-6" onSubmit={handleSubmit}>
          {items.map((item) => {
            return (
              <div key={item.name}>
                <label htmlFor={item.name} className="block text-sm font-medium leading-6 text-gray-900">
                  {item.title}
                </label>

                <div className="mt-2">
                  {item.type === 'date' ? (
                    <DatePicker 
                      selected={data && data[item.name] ? new Date(data[item.name]) : ""}
                      onChange={date => handleDateChange(date, item.name)}
                      dateFormat={"yyyy-MM-dd"}
                      className='pl-2 w-full rounded-md block border border-gray-300 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 sm:text-sm sm:leading-6'
                    />                
                  ) : item.type === 'dateArray' ? (
                    <DateArrayInput
                      dates={data && data[item.name] ? (Array.isArray(data[item.name]) ? data[item.name] : [data[item.name]]) : []}
                      onAddDate={() => handleDateArrayChange && handleDateArrayChange(item.name, 'add')}
                      onRemoveDate={(index) => handleDateArrayChange && handleDateArrayChange(item.name, 'remove', index)}
                      onUpdateDate={(index, date) => handleDateArrayChange && handleDateArrayChange(item.name, 'update', index, date)}
                    />
                  ) : item.type === 'select' ? (
                    <select 
                      id={item.name}
                      name={item.name}
                      value={data ? data[item.name] : ""}
                      onChange={handleInputChange}
                      className="px-3 py-2.5 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    >
                      <option value="" disabled>Please select</option>
                      {item.options.map(option => (
                          <option key={option.value} value={option.value}>
                              {option.label}
                          </option>
                      ))}
                    </select>
                  ) : item.type === 'boolean' ? (
                    <input
                      id={item.name}
                      name={item.name}
                      type="checkbox"
                      checked={data ? !!data[item.name] : false}
                      onChange={handleInputChange}
                      className="rounded text-indigo-600 focus:ring-indigo-500"
                    />
                  ) : item.type === 'textarea' ? (
                    <textarea
                      id={item.name}
                      name={item.name}
                      value={data ? data[item.name] : ""}
                      onChange={handleInputChange}
                      rows={4}
                      className="px-3 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  ) : (
                    <input
                      id={item.name}
                      name={item.name}
                      type={item.type}
                      step={item.step || undefined}
                      /* Optional per-field lock. Submission reads React state, not the
                         DOM, so a disabled field is still sent — it just cannot be typed
                         into. Undefined for every existing caller, so nothing changes. */
                      disabled={item.disabled || undefined}
                      value={item.type !== 'checkbox' ? (data?.[item.name] ?? '') : undefined}
                      checked={item.type === 'checkbox' ? !!data?.[item.name] : undefined}
                      onChange={handleInputChange}
                      className={
                          item.type === 'checkbox'
                              ? ''
                              : `px-3 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6${item.disabled ? ' bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`
                      }
                  />
                  )}
                  {item.hint && (
                    <p className="mt-1 text-xs text-gray-500">{item.hint}</p>
                  )}
                </div>
              </div>
            )
          })}
          <div className='flex space-x-5'>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (isEditMode ? 'Updating...' : 'Registering...') : (submitLabel || (isEditMode ? 'Update' : 'Register'))}
              </button>
              <button
                onClick={(closeForm)}
                className="flex w-full justify-center rounded-md bg-gray-400 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  )
}

export default AddEditForm

  